/* eslint-disable sonarjs/cognitive-complexity */
import {
  GridColDef,
  GRID_CHECKBOX_SELECTION_COL_DEF,
} from '@mui/x-data-grid-pro';
import { useTheme, useMediaQuery } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateOrderItemSerialNumber,
  updateOrderItemTrackingNumber,
  updateOrderItemCarrier,
  selectOrderItemChanges,
} from 'store/slices/shipping/orderItemChanges';
import {
  renderShipmentStatusPill,
  getShipmentInfo,
} from 'pages/Shipping/utils';
import { ShipmentCarrier } from 'api/rest/shipmentCarriers/types';
import { SHIPPING_BLANK_TABLE_CELL_VALUE } from 'pages/Shipping/constants';
import { getItemProductTitle } from 'pages/Orders/utils';
import moment from 'moment';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import LaunchIcon from '@mui/icons-material/Launch';

interface UseGetShippingColumnsProps {
  carriers: ShipmentCarrier[];
  loadingCarriers: boolean;
}

const useGetShippingColumns = ({
  carriers,
  loadingCarriers,
}: UseGetShippingColumnsProps): GridColDef[] => {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const dispatch = useDispatch();

  // Get the order item changes from Redux store once at the component level
  const orderItemChanges = useSelector(selectOrderItemChanges);

  return [
    GRID_CHECKBOX_SELECTION_COL_DEF,
    {
      field: 'orderNumber',
      headerName: 'Order number',
      flex: 1,
      sortable: false,
      filterable: false,
      valueGetter({ row }) {
        // Get order number from the new nested structure
        return row?.order?.orderNumber ?? SHIPPING_BLANK_TABLE_CELL_VALUE;
      },
    },
    {
      field: 'orderItemNumber',
      headerName: 'Order item number',
      flex: 1,
      sortable: false,
      filterable: false,
      valueGetter({ row }) {
        return row?.orderItemNumber ?? SHIPPING_BLANK_TABLE_CELL_VALUE;
      },
    },
    {
      field: 'productName',
      headerName: 'Title',
      flex: isLargeScreen ? 2 : 1,
      sortable: false,
      filterable: false,
      valueGetter({ row }) {
        return getItemProductTitle(row, row?.order?.orderType?.name);
      },
    },
    {
      field: 'serialNumberField',
      headerName: 'Serial number',
      flex: isLargeScreen ? 2 : 1,
      sortable: false,
      filterable: false,
      editable: true,
      valueGetter(params) {
        // Check if there's a pending change for this order item
        const id = String(params.row.id);
        const orderItemChange = orderItemChanges[id];
        if (orderItemChange?.serialNumber !== undefined) {
          return orderItemChange.serialNumber;
        }
        return params?.row?.serialNumber ?? '';
      },
      cellClassName: (params) => {
        // Check if this cell has been edited
        const id = String(params.id);
        const orderItemChange = orderItemChanges[id];
        return `editable-cell ${
          orderItemChange?.serialNumber !== undefined ? 'edited-cell' : ''
        }`;
      },
      preProcessEditCellProps: (params) => {
        const { id, props, row } = params;
        const { value } = props;

        dispatch(
          updateOrderItemSerialNumber({
            id: String(id),
            orderId: row.order.id,
            serialNumber: value,
            originalOrderItem: row,
          })
        );
        return { ...props };
      },
    },
    {
      field: 'shipmentStatus',
      headerName: 'Shipment status',
      flex: 1,
      sortable: false,
      filterable: false,
      valueGetter(params) {
        const shipmentInfo = getShipmentInfo(params.row);
        return shipmentInfo.status ?? 'PENDING';
      },
      renderCell: (params) => {
        const shipmentInfo = getShipmentInfo(params.row);
        // Use the shared utility function to render the shipment status pill
        return renderShipmentStatusPill(
          shipmentInfo.status || undefined,
          shipmentInfo.substatus || undefined,
          shipmentInfo.substatusDescription || undefined,
          theme,
          { toolTipPlacement: 'top' } // Override tooltip placement for table context
        );
      },
    },
    {
      field: 'trackingNumber',
      headerName: 'Tracking number',
      flex: isLargeScreen ? 1.5 : 1,
      sortable: false,
      filterable: false,
      editable: true,
      valueGetter(params) {
        // Check if there's a pending change for this order item
        const id = String(params.row.id);
        const orderItemChange = orderItemChanges[id];
        if (orderItemChange?.trackingNumber !== undefined) {
          return orderItemChange.trackingNumber;
        }

        // Get tracking number using the utility function
        const shipmentInfo = getShipmentInfo(params.row);
        return shipmentInfo.trackingNumber || '';
      },
      cellClassName: (params) => {
        // Check if this cell has been edited
        const id = String(params.id);
        const orderItemChange = orderItemChanges[id];
        return `editable-cell ${
          orderItemChange?.trackingNumber !== undefined ? 'edited-cell' : ''
        }`;
      },
      preProcessEditCellProps: (params) => {
        const { id, props, row } = params;
        const { value } = props;

        dispatch(
          updateOrderItemTrackingNumber({
            id: String(id),
            orderId: row.order.id,
            trackingNumber: value || null,
            originalOrderItem: row,
          })
        );
        return { ...props };
      },
    },
    {
      field: 'carrier',
      headerName: 'Tracking carrier',
      flex: 1,
      sortable: false,
      filterable: false,
      editable: !loadingCarriers,
      type: 'singleSelect',
      valueOptions: loadingCarriers
        ? []
        : carriers.map((carrier) => carrier.name.toUpperCase()),
      valueGetter({ row }) {
        // Check if there's a pending change for this order item
        const id = String(row.id);
        const orderItemChange = orderItemChanges[id];
        if (orderItemChange?.carrierId !== undefined) {
          const carrier = carriers?.find(
            (c) => c.id === orderItemChange.carrierId
          );
          return carrier?.name.toUpperCase() ?? '';
        }

        // Get shipment info using the utility function
        const shipmentInfo = getShipmentInfo(row);
        const carrierName = carriers?.find(
          (c) => c.id === shipmentInfo.shipmentCarrierId
        )?.name;
        return (carrierName ?? SHIPPING_BLANK_TABLE_CELL_VALUE).toUpperCase();
      },
      preProcessEditCellProps: (params) => {
        const { props, row } = params;
        const { value } = props;

        if (loadingCarriers) return { ...props, error: true };

        // Find carrier ID from the name
        const carrier = carriers.find(
          (c) => c.name.toUpperCase() === value?.toUpperCase()
        );
        if (!carrier) return { ...props, error: true };

        dispatch(
          updateOrderItemCarrier({
            id: String(row.id),
            orderId: row.order.id,
            carrierId: carrier.id,
            originalOrderItem: row,
          })
        );

        return { ...props };
      },
      cellClassName: (params) => {
        const id = String(params.id);
        const orderItemChange = orderItemChanges[id];
        return `carrier-column editable-cell ${
          orderItemChange?.carrierId !== undefined ? 'edited-cell' : ''
        }`;
      },
    },
    {
      field: 'trackingLink',
      headerName: '',
      width: 40,
      minWidth: 40,
      maxWidth: 40,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const shipmentInfo = getShipmentInfo(params.row);
        if (!shipmentInfo.trackingLink) {
          return null;
        }
        return (
          <Tooltip title={shipmentInfo.trackingLink} placement="top" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                // Since we've already checked that trackingLink exists and is not null above
                window.open(shipmentInfo.trackingLink as string, '_blank');
              }}
              data-testid="tracking-link-button"
            >
              <LaunchIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      },
    },
    {
      field: 'estimatedDelivery',
      headerName: 'Estimated delivery',
      flex: 1,
      sortable: false,
      filterable: false,
      valueGetter({ row }) {
        const shipmentInfo = getShipmentInfo(row);
        if (!shipmentInfo.expectedDeliveryDate) {
          return SHIPPING_BLANK_TABLE_CELL_VALUE;
        }
        return moment.utc(shipmentInfo.expectedDeliveryDate).format('MMM D');
      },
      renderCell: (params) => {
        const shipmentInfo = getShipmentInfo(params.row);
        if (!shipmentInfo.expectedDeliveryDate) {
          return SHIPPING_BLANK_TABLE_CELL_VALUE;
        }
        const date = moment.utc(shipmentInfo.expectedDeliveryDate);
        return (
          <Tooltip title={date.format('MMM D, YYYY')} placement="top" arrow>
            <div style={{ cursor: 'pointer' }}>{date.format('MMM D')}</div>
          </Tooltip>
        );
      },
    },
  ];
};

export default useGetShippingColumns;
